import React, { useEffect, useState } from 'react';
import { FaCrop } from 'react-icons/fa';
import Card from '../../components/Card/Card';
import './screenshot-picker.css';
import { createLogger } from '../../services/Debug';
export default function ScreenshotPicker({ onClose, onScreenshotDone, dataUrl: initialDataUrl }) {
  const [sources, setSources] = useState([]);
  const [dataUrl, setDataUrl] = useState(initialDataUrl);
  const log = createLogger('ScreenshotPicker');
  const handleScreenshot = (screenshotDataUrl) => {
    if (screenshotDataUrl) {
      onScreenshotDone(screenshotDataUrl);
    }
    onClose();
  };

  useEffect(() => {
    if (dataUrl) {
      handleScreenshot(dataUrl);
      return;
    }

    loadSources();

    // Pas de raccourcis clavier locaux — les hotkeys sont gérés par Electron
    return undefined;
  }, []); // Charger les sources une seule fois au montage

  async function loadSources() {
    try {
      if (!window.electronAPI) return;
      const srcs = await window.electronAPI.listAvailableSources();
      
      // Trier les sources : écrans en premier (triés par numéro), puis les fenêtres
      const sortedSources = [...srcs].sort((a, b) => {
        // Vérifier si c'est un écran (Screen X, Écran X, Entire Screen, etc.)
        const aIsScreen = /screen|écran/i.test(a.name);
        const bIsScreen = /screen|écran/i.test(b.name);

        // Les écrans toujours en premier
        if (aIsScreen && !bIsScreen) return -1;
        if (!aIsScreen && bIsScreen) return 1;

        // Si les deux sont des écrans, trier par numéro
        if (aIsScreen && bIsScreen) {
          const aNum = parseInt(a.name.match(/\d+/)?.[0] || '0', 10);
          const bNum = parseInt(b.name.match(/\d+/)?.[0] || '0', 10);
          return aNum - bNum;
        }

        // Pour les fenêtres, trier par nom
        return a.name.localeCompare(b.name);
      });

      setSources(sortedSources);
      
      // Si un seul écran détecté, lancer directement la sélection partielle
      if (sortedSources.length === 1) {
        handleCapturePart(sortedSources[0].id);
      }
    } catch (err) {
      log.error('Error listing sources =>', err);
      onClose();
    }
  }

  async function handleCapturePart(sourceId) {
    try {
      const capturedDataUrl = await window.electronAPI.captureRegionScreenshotFromId(sourceId);
      handleScreenshot(capturedDataUrl);
    } catch (err) {
      log.error('Error capturing region =>', err);
      onClose();
    }
  }

  async function handleCaptureFull(sourceId) {
    try {
      const capturedDataUrl = await window.electronAPI.captureScreenshotFromId(sourceId);
      handleScreenshot(capturedDataUrl);
    } catch (err) {
      log.error('Error capturing full screenshot =>', err);
      onClose();
    }
  }

  const handleOverlayClick = (e) => {
    // Si on clique sur l'overlay lui-même (pas ses enfants)
    if (e.target.className === 'dictaphone-overlay screenshot-picker-overlay') {
      onClose();
    }
  };

  // The loading UI is removed. The component will close immediately after capture.
  if (sources.length > 1) {
    return (
      <div className="dictaphone-overlay screenshot-picker-overlay" onClick={handleOverlayClick}>
        <Card className="dictaphone-overlay-card screenshot-picker-card">
          <div className="screenshot-picker-menu">
            {(() => {
              let screenShortcutCounter = 0;
              return sources.map((src, index) => {
                const isScreen = /screen|écran/i.test(src.name.toLowerCase());
                let shortcutLabel = null;
                if (isScreen && screenShortcutCounter < 3) {
                  shortcutLabel = `Ctrl+Shift+${screenShortcutCounter + 1}`;
                  screenShortcutCounter += 1;
                }

                return (
                  <div
                    className="screenshot-picker-menu-item"
                    key={src.id}
                    onClick={() => handleCaptureFull(src.id)}
                  >
                    <span className="screenshot-picker-menu-text">
                      {src.name.length > 50 ? `${src.name.substring(0, 50)}...` : src.name}
                    </span>
                    <div className="screenshot-picker-actions">
                      {shortcutLabel && (
                        <span className="screenshot-picker-shortcut">
                          {shortcutLabel}
                        </span>
                      )}
                      <button
                        className="screenshot-picker-partial-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCapturePart(src.id);
                        }}
                        title="Capture partielle"
                      >
                        <FaCrop />
                      </button>
                    </div>
                  </div>
                );
              });
            })()}
          </div>
        </Card>
      </div>
    );
  }

  // Si un seul écran, on retourne null
  return null;
}